import { useState, useCallback, useRef } from 'react';
import { useRecoilValue } from 'recoil';
import { encodeFunctionData } from 'viem';
import {
  userSmartAccount,
  userSmartAccountClient,
  userSmartAccountUsdtBalance,
} from '../../RecoilAtom';
import { useCheckAllowance } from '../../../hooks/dataSender/Allowance';
import { convertToMwei, formatSmartDecimal, handleGasFeeWithBuffer, handleGasFeeWithPreparedCalls, sendGasFeeAsUsdt, isSolvencyGateError } from '../../../utils/helpers';
import {
  ALCHEMY_API_KEY,
  BIG_NUMBER_APPROVAL,
  DISPUTE_INITIAL_TIMER,
  IPFS_GATEWAY,
  NEW_FACTORY_CONTRACT,
  RAIN_ARBI_CONTRACT_ADDRESS,
  usdr_token,
} from '../../../utils/Enviroment';
import {
  CREATE_MARKET_TIME_BUFFER,
  MAX_BUFFER_PERCENTAGE,
  TOKEN_NAME_TO_CONTRACT_ADDRESS,
  TOKEN_USDR,
  USDT_MOCK_VALUE,
  ZERO_ADDRESS,
} from '../../../utils/constants';
import CreatePoolAbi from '../../../utils/CreatePoolAbi.json';
import BalanceOfAbi from '../../../utils/BalanceOfAbi.json';
import { normalizeOutcomesForContract, outcomesToBarValues } from '../helpers';
import { notifyError } from '../../Toast';
import { DEFAULT_ERROR_HEADING, GAS_FEES_ERROR } from '../../../utils/ConstantMessages';
import { Alchemy, Network } from 'alchemy-sdk';

const DEBOUNCE_DELAY = 600;

const alchemy = new Alchemy({
  apiKey: ALCHEMY_API_KEY,
  network: Network.ARB_MAINNET,
});

export const useGasEstimation = ({ marketData }) => {
  const [gasFeeInUsdt, setGasFeeInUsdt] = useState(0);
  const [gasFeesSpinner, setGasFeesSpinner] = useState(false);
  const [liquidityAmount, setLiquidityAmount] = useState(0);
  const [gasError, setGasError] = useState(null);
  const [isSolvencyLimitReached, setIsSolvencyLimitReached] = useState(false);

  const smartAccount = useRecoilValue(userSmartAccount);
  const smartAccountClient = useRecoilValue(userSmartAccountClient);
  const smartAccountUsdtBalance = useRecoilValue(userSmartAccountUsdtBalance);
  const { getAllowanceByAddress } = useCheckAllowance();

  const debounceRef = useRef(null);
  // Keep a ref to the latest marketData so the debounced callback never uses a stale closure
  const marketDataRef = useRef(marketData);
  marketDataRef.current = marketData;

  const getEstimatedTxGasFee = useCallback(
    async (callArgs, isAllowanceNeeded) => {
      if (!smartAccount) {
        setGasError('smart-account-not-ready');
        return;
      }
      if (isAllowanceNeeded && !smartAccountClient) {
        setGasError('smart-account-not-ready');
        return;
      }
      if (USDT_MOCK_VALUE > +smartAccountUsdtBalance) {
        notifyError(DEFAULT_ERROR_HEADING, GAS_FEES_ERROR);
        setGasFeesSpinner(false);
        setGasFeeInUsdt(0);
        setGasError('insufficient-gas-balance');
        return;
      }
      try {
        setGasError(null);
        if (isAllowanceNeeded) {
          return await handleGasFeeWithPreparedCalls(
            smartAccountClient,
            smartAccount,
            callArgs
          );
        }
        return await handleGasFeeWithBuffer(smartAccount, callArgs, alchemy);
      } catch (err) {
        setIsSolvencyLimitReached(isSolvencyGateError(err));
        setGasFeeInUsdt(0);
        setGasError('estimation-failed');
      }
    },
    [smartAccount, smartAccountClient, smartAccountUsdtBalance]
  );

  const calculateGas = useCallback(
    async (value, currency = 'usdr') => {
      if (+value === 0 || value === '') {
        setGasFeeInUsdt(0);
        setGasError(null);
        return;
      }

      // Always read the latest marketData from the ref (not the stale closure)
      const currentMarketData = marketDataRef.current;

      // Guard: smart account must be initialized before gas estimation
      if (!smartAccount) {
        setGasError('smart-account-not-ready');
        setGasFeesSpinner(false);
        return;
      }

      // Guard: outcomes must be populated for contract simulation
      const outcomes = currentMarketData.outcomes;
      if (!outcomes || outcomes.length === 0) {
        setGasError('missing-outcomes');
        setGasFeesSpinner(false);
        return;
      }

      // Normalize and verify we have at least 2 outcomes (1 user + NONE)
      const simOutcomes = normalizeOutcomesForContract(outcomes);
      if (simOutcomes.length < 2) {
        setGasError('missing-outcomes');
        setGasFeesSpinner(false);
        return;
      }

      const curUpper = currency.toUpperCase();
      let inputAmountWei;
      if (curUpper === TOKEN_USDR && +value >= +smartAccountUsdtBalance) {
        const adjusted = value - USDT_MOCK_VALUE * MAX_BUFFER_PERCENTAGE;
        inputAmountWei = convertToMwei(adjusted, 'ether');
      } else {
        inputAmountWei = convertToMwei(value, 'ether');
      }

      try {
        setGasFeesSpinner(true);
        setGasError(null);

        const startNowDate = new Date(Date.now() + CREATE_MARKET_TIME_BUFFER);
        const startTime = Math.floor(startNowDate.getTime() / 1000);
        const endTime = currentMarketData.resolutionDate
          ? Math.floor(new Date(currentMarketData.resolutionDate).getTime() / 1000)
          : Math.floor((Date.now() + 86400000) / 1000);

        const barValues = outcomesToBarValues(simOutcomes);

        const contractParams = [
          currentMarketData.isPublic,
          false,
          smartAccount,
          ZERO_ADDRESS,
          startTime,
          endTime,
          simOutcomes.length,
          DISPUTE_INITIAL_TIMER,
          `${IPFS_GATEWAY}/QmVweBcq63YQsA41KCYs8xSxXdeKeJUtkgsohPBMvWhkAt/`,
          parseFloat(inputAmountWei || '0'),
          barValues,
          smartAccount,
          TOKEN_NAME_TO_CONTRACT_ADDRESS[curUpper],
        ];

        const usdtAllowance = await getAllowanceByAddress(usdr_token, NEW_FACTORY_CONTRACT);
        const rainAllowance = await getAllowanceByAddress(RAIN_ARBI_CONTRACT_ADDRESS, NEW_FACTORY_CONTRACT);
        const isUsdtApprovalNeeded = usdtAllowance < contractParams[9];
        const isRainApprovalNeeded = rainAllowance < contractParams[9];
        const isAnyApprovalNeeded = isUsdtApprovalNeeded || isRainApprovalNeeded;

        const callArgs = [];
        if (isUsdtApprovalNeeded) {
          callArgs.push({
            to: usdr_token,
            data: encodeFunctionData({
              abi: BalanceOfAbi,
              functionName: 'approve',
              args: [NEW_FACTORY_CONTRACT, BIG_NUMBER_APPROVAL],
            }),
          });
        }
        if (isRainApprovalNeeded) {
          callArgs.push({
            to: RAIN_ARBI_CONTRACT_ADDRESS,
            data: encodeFunctionData({
              abi: BalanceOfAbi,
              functionName: 'approve',
              args: [NEW_FACTORY_CONTRACT, BIG_NUMBER_APPROVAL],
            }),
          });
        }
        callArgs.push({
          to: NEW_FACTORY_CONTRACT,
          data: encodeFunctionData({
            abi: CreatePoolAbi,
            functionName: 'createPool',
            args: [contractParams],
          }),
        });
        callArgs.push({
          to: usdr_token,
          data: sendGasFeeAsUsdt(USDT_MOCK_VALUE),
        });

        const gasFee = await getEstimatedTxGasFee(callArgs, isAnyApprovalNeeded);

        if (curUpper === TOKEN_USDR && +value >= +smartAccountUsdtBalance) {
          setLiquidityAmount(formatSmartDecimal(value - +gasFee * MAX_BUFFER_PERCENTAGE));
        }

        setGasFeeInUsdt(gasFee || 0);
        setGasFeesSpinner(false);
      } catch (err) {
        notifyError(DEFAULT_ERROR_HEADING, GAS_FEES_ERROR);
        setGasFeeInUsdt(0);
        setGasFeesSpinner(false);
        setGasError('estimation-failed');
      }
    },
    [smartAccount, smartAccountUsdtBalance, getAllowanceByAddress, getEstimatedTxGasFee]
  );

  const debouncedCalculateGas = useCallback(
    (value, currency) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (+value === 0 || value === '') {
        setGasFeeInUsdt(0);
        setGasFeesSpinner(false);
        return;
      }
      setGasFeesSpinner(true);
      debounceRef.current = setTimeout(() => {
        calculateGas(value, currency);
      }, DEBOUNCE_DELAY);
    },
    [calculateGas]
  );

  return {
    gasFeeInUsdt,
    gasFeesSpinner,
    gasError,
    liquidityAmount,
    setLiquidityAmount,
    calculateGas: debouncedCalculateGas,
    isSolvencyLimitReached,
  };
};
